// Base domains this app is served on. Add "localhost" for local dev
// convenience (e.g. testing the bare app with zero subdomain logic).
const BASE_DOMAINS = ['dev.irancharsouq.ir', 'localhost', 'irancharsouq.local'];
const RESERVED_NAMES = new Set(['www', 'api', 'admin']);

/**
 * Returns the vendor slug if hostname is <slug>.<base-domain>,
 * or null if it's the bare domain, a reserved name, or an unrecognized host.
 */
export function getVendorSlugFromHostname(hostname) {
  const host = hostname.split(':')[0]; // strip port, e.g. "x.localhost:5173"

  for (const base of BASE_DOMAINS) {
    if (host === base || host === `www.${base}`) return null;
    const suffix = `.${base}`;
    if (host.endsWith(suffix)) {
      const sub = host.slice(0, -suffix.length);
      if (sub && !RESERVED_NAMES.has(sub)) return sub;
    }
  }
  return null;
}

/**
 * Finds which BASE_DOMAINS entry the current hostname belongs to
 * (bare or subdomain), so URL-builders below can construct a sibling
 * URL on the same base domain, preserving protocol/port.
 */
function getMatchedBaseDomain(hostname) {
  const host = hostname.split(':')[0];
  for (const base of BASE_DOMAINS) {
    if (host === base || host === `www.${base}` || host.endsWith(`.${base}`)) {
      return base;
    }
  }
  return host; // fallback: treat current host as its own base
}

/**
 * Returns the domain string to use for cookies that need to be shared
 * across the base domain and every vendor subdomain (e.g. ".dev.irancharsouq.ir").
 * Returns undefined for hosts that can't take a leading-dot Domain
 * (localhost, bare IPs) — callers should omit the domain option in that case.
 */
export function getSharedCookieDomain() {
  const base = getMatchedBaseDomain(window.location.hostname);
  if (base === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(base)) return undefined;
  return `.${base}`;
}

/**
 * Builds a full URL back to the bare domain's homepage, preserving
 * the current protocol and port. Used for "Home" navigation away
 * from a vendor's subdomain back to the main platform.
 */
export function getBareDomainUrl() {
  const base = getMatchedBaseDomain(window.location.hostname);
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${window.location.protocol}//${base}${port}/`;
}

export function getVendorSubdomainUrl(slug) {
  const base = getMatchedBaseDomain(window.location.hostname);
  const port = window.location.port ? `:${window.location.port}` : '';
  return `${window.location.protocol}//${slug}.${base}${port}/`;
}